import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useRef } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney, formatNumber, localized } from "../lib/format";
import { HallFormDialog, MenuFormDialog, type EditableHall } from "../components/HallFormDialog";
import { HallPhotosDialog } from "../components/HallPhotosDialog";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

interface PriceSuggestion {
  occupancyPct: number;
  peakWeekday: string | null;
  rationale: string;
  suggestions: {
    menuId: string;
    menuName: Record<string, string>;
    currentPerGuestMinor: number;
    suggestedPerGuestMinor: number;
    adjustPct: number;
  }[];
}

interface HallDoc {
  id: string;
  name: Record<string, string>;
  status: string;
  cityId: string;
  hallType: string;
  capacity?: { total: number; mens?: number; womens?: number };
  priceRange?: { minPerGuestMinor: number; currency: string };
  rating?: { avg: number; count: number };
  coverUrl?: string;
  galleryCount?: number;
  hallFeeMinor?: number;
  depositPct?: number;
  instantBooking?: boolean;
  amenities?: string[];
}

const STATUS_META: Record<string, { labelKey: StringKey; color: "default" | "success" | "warning" }> = {
  draft: { labelKey: "hall_status_draft", color: "default" },
  pending_review: { labelKey: "hall_status_pending_review", color: "warning" },
  published: { labelKey: "hall_status_published", color: "success" },
  suspended: { labelKey: "hall_status_suspended", color: "default" },
};

export function HallsPage() {
  const { orgId } = useSession();
  const t = useT();
  const [halls, setHalls] = useState<HallDoc[]>([]);
  const [suggestion, setSuggestion] = useState<{ hallName: string; data: PriceSuggestion } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editHall, setEditHall] = useState<EditableHall | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [photosFor, setPhotosFor] = useState<HallDoc | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetRef = useRef<string | null>(null);

  function pickPhoto(hallId: string) {
    uploadTargetRef.current = hallId;
    fileInputRef.current?.click();
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const hallId = uploadTargetRef.current;
    e.target.value = "";
    if (!file || !hallId || !orgId) return;
    if (file.size > 15 * 1024 * 1024) {
      setToast(t("halls_toast_photo_too_big"));
      return;
    }
    setUploadingId(hallId);
    try {
      // Org-scoped path so Storage rules can verify tenancy from the token
      // claim (Storage rules can't read Firestore).
      const path = `orgs/${orgId}/halls/${hallId}/${Date.now()}_${file.name.replace(/[^\w.-]/g, "_")}`;
      const dest = storageRef(storage, path);
      await uploadBytes(dest, file, { contentType: file.type });
      const url = await getDownloadURL(dest);
      await api(`/orgs/${orgId}/halls/${hallId}/media`, {
        method: "POST",
        body: { url, storagePath: path },
      });
      setToast(t("halls_toast_photo_added"));
    } catch (err) {
      setToast(err instanceof Error ? err.message : t("halls_toast_photo_failed"));
    } finally {
      setUploadingId(null);
    }
  }

  async function publish(hallId: string) {
    try {
      await api(`/orgs/${orgId}/halls/${hallId}/publish`, { method: "POST" });
      setToast(t("halls_toast_sent_publish"));
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    }
  }

  async function loadSuggestion(hall: HallDoc) {
    try {
      const data = await api<PriceSuggestion>(
        `/orgs/${orgId}/ai/price-suggestions?hallId=${hall.id}`,
      );
      setSuggestion({ hallName: localized(hall.name), data });
    } catch {
      setSuggestion(null);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "halls"), where("orgId", "==", orgId));
    return onSnapshot(q, (snap) => {
      setHalls(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<HallDoc, "id">) })));
    });
  }, [orgId]);

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t("halls_title")}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
          {t("halls_add")}
        </Button>
      </Stack>
      <Grid container spacing={2}>
        {halls.map((hall) => {
          const status = STATUS_META[hall.status];
          const statusLabel = status ? t(status.labelKey) : hall.status;
          const statusColor = status?.color ?? ("default" as const);
          return (
            <Grid item xs={12} sm={6} md={4} key={hall.id}>
              <Card>
                {hall.coverUrl && (
                  <CardMedia component="img" height="160" image={hall.coverUrl} />
                )}
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{localized(hall.name)}</Typography>
                    <Chip size="small" label={statusLabel} color={statusColor} />
                  </Stack>
                  <Typography color="text.secondary" variant="body2" mt={0.5}>
                    {t("halls_capacity", { total: hall.capacity?.total ?? "—" })}
                    {hall.priceRange
                      ? " " +
                        t("halls_price_from", {
                          price: formatMoney(hall.priceRange.minPerGuestMinor, hall.priceRange.currency),
                        })
                      : ""}
                  </Typography>
                  {hall.rating && hall.rating.count > 0 && (
                    <Typography variant="body2" color="secondary.main" mt={0.5}>
                      {t("halls_rating", { avg: hall.rating.avg, count: hall.rating.count })}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() =>
                        setEditHall({
                          id: hall.id,
                          name: hall.name,
                          cityId: hall.cityId,
                          hallType: hall.hallType,
                          capacity: hall.capacity,
                          hallFeeMinor: hall.hallFeeMinor,
                          depositPct: hall.depositPct,
                          instantBooking: hall.instantBooking,
                          amenities: hall.amenities,
                        })
                      }
                    >
                      {t("hall_edit")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddPhotoAlternateOutlinedIcon />}
                      disabled={uploadingId === hall.id}
                      onClick={() => pickPhoto(hall.id)}
                    >
                      {uploadingId === hall.id ? t("hall_uploading") : t("hall_add_photo")}
                    </Button>
                    <Button size="small" startIcon={<PhotoLibraryOutlinedIcon />} onClick={() => setPhotosFor(hall)}>
                      {t("hall_photos")}
                      {hall.galleryCount ? ` (${formatNumber(hall.galleryCount)})` : ""}
                    </Button>
                    <Button size="small" startIcon={<RestaurantMenuOutlinedIcon />} onClick={() => setMenuFor(hall.id)}>
                      {t("hall_add_menu")}
                    </Button>
                    {hall.status === "draft" && (
                      <Button size="small" variant="contained" onClick={() => publish(hall.id)}>
                        {t("hall_submit_publish")}
                      </Button>
                    )}
                    <Button size="small" startIcon={<AutoAwesomeOutlinedIcon />} onClick={() => loadSuggestion(hall)}>
                      {t("hall_price_suggestion")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {halls.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" textAlign="center" py={6}>
              {t("halls_empty")}
            </Typography>
          </Grid>
        )}
      </Grid>

      {orgId && (
        <>
          <HallPhotosDialog
            orgId={orgId}
            hallId={photosFor?.id ?? null}
            hallName={photosFor ? localized(photosFor.name) : ""}
            open={photosFor !== null}
            onClose={() => setPhotosFor(null)}
            onUpload={() => photosFor && pickPhoto(photosFor.id)}
            uploading={uploadingId !== null && uploadingId === photosFor?.id}
          />
          <HallFormDialog
            orgId={orgId}
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSaved={(hallId) => {
              setToast(t("halls_toast_hall_created"));
              setMenuFor(hallId);
            }}
          />
          <HallFormDialog
            orgId={orgId}
            open={!!editHall}
            hall={editHall}
            onClose={() => setEditHall(null)}
            onSaved={() => setToast(t("halls_toast_hall_saved"))}
          />
          <MenuFormDialog
            orgId={orgId}
            hallId={menuFor}
            onClose={() => setMenuFor(null)}
            onCreated={() => setToast(t("halls_toast_menu_created"))}
          />
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />
      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} closeText={t("common_close")} severity="info">
          {toast}
        </Alert>
      </Snackbar>

      <Dialog open={!!suggestion} onClose={() => setSuggestion(null)} maxWidth="sm" fullWidth>
        {suggestion && (
          <>
            <DialogTitle>{t("halls_price_dialog_title", { hall: suggestion.hallName })}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Alert severity="info">
                  {t("halls_price_occupancy", { pct: suggestion.data.occupancyPct })}
                  {suggestion.data.peakWeekday &&
                    ` ${t("halls_price_peak", { day: suggestion.data.peakWeekday })}`}
                  <br />
                  {suggestion.data.rationale}
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("halls_price_col_menu")}</TableCell>
                      <TableCell>{t("halls_price_col_current")}</TableCell>
                      <TableCell>{t("halls_price_col_suggested")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestion.data.suggestions.map((s) => (
                      <TableRow key={s.menuId}>
                        <TableCell>{localized(s.menuName)}</TableCell>
                        <TableCell>{formatMoney(s.currentPerGuestMinor)}</TableCell>
                        <TableCell>
                          {formatMoney(s.suggestedPerGuestMinor)}
                          {s.adjustPct !== 0 && (
                            <Chip
                              size="small"
                              sx={{ ml: 1 }}
                              color={s.adjustPct > 0 ? "success" : "warning"}
                              label={`${s.adjustPct > 0 ? "+" : ""}${formatNumber(s.adjustPct)}٪`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary">
                  {t("halls_price_footnote")}
                </Typography>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}
